import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../../shared/lib/backend-errors';
import {
  requireList,
  requireObject,
  UNREADABLE_RESPONSE_CODE,
} from '../../../shared/lib/response-shape';
import { assertSignableText, buildMultipartBody } from '../../../shared/lib/multipart';
import { bytesToHex } from '../../../shared/lib/hex';
import type { ScopeContext } from '../../auth/model/types';

const backendClient = new BackendApiClient();

/** b-cell-attachment caps an upload at 30 MiB (`oversized-content` past that). */
export const MAX_ATTACHMENT_BYTES = 30 * 1024 * 1024;

/**
 * CSV only.
 *
 * The upload is signed as UTF-8 text (see `shared/lib/multipart.ts`), so a binary
 * workbook cannot be sent at all today — XLSX and PDF wait on the backend gaining a
 * signable binary path. The picker advertises the same restriction through `accept`,
 * but `accept` is a filter and not a guarantee, so the check below is what enforces it.
 */
export const CSV_ACCEPT = '.csv,text/csv';

export type AttachmentStatus = 'created' | 'success' | 'rejected' | 'failed';

/** Statuses the scan will not move away from — the poll's stopping condition. */
export const TERMINAL_ATTACHMENT_STATUSES: AttachmentStatus[] = ['success', 'rejected', 'failed'];

export function isTerminalAttachmentStatus(status: AttachmentStatus | undefined): boolean {
  return status != null && TERMINAL_ATTACHMENT_STATUSES.includes(status);
}

/**
 * Every status except `failed` — mirrors the backend's own `NON_FAILED_ATTACHMENT_STATUSES`
 * (`b-cell-attachment` `label/attachment.py`), which is exactly the set the digest guard
 * still allows a re-store against. A `failed` attachment is a dead end: the bytes never
 * became servable, and adopting it as if retryable would present that dead end as
 * recorded work. Passed to `findAttachmentByUniqueId` so a `failed` record is never
 * mistaken for a usable adoption target.
 */
export const ADOPTABLE_ATTACHMENT_STATUSES: AttachmentStatus[] = ['created', 'success', 'rejected'];

export interface Attachment {
  id: string;
  status: AttachmentStatus;
  scope_id: string;
  scope_type: string;
  file_name: string;
  content_type: string;
  byte_size: number;
  unique_id?: string | null;
  tags: string[];
  created: string;
}

/**
 * Why a chosen file cannot be uploaded. The UI maps each code to its own message —
 * refusing a file without saying which rule it broke leaves the user guessing.
 */
export type CsvRejectionCode = 'not-csv' | 'too-large' | 'empty-file' | 'not-utf8';

export class CsvFileError extends Error {
  constructor(public rejection: CsvRejectionCode) {
    super(rejection);
    this.name = 'CsvFileError';
  }
}

function hasCsvName(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.csv');
}

/**
 * The rejection this file would earn, or `null` if it is acceptable.
 *
 * Synchronous and byte-free so the picker can report a wrong file the moment it is
 * chosen, rather than after a read the user waits through. The UTF-8 rule cannot be
 * decided here — it needs the content — so it lives in `readCsvText`.
 */
export function csvRejection(file: File): CsvRejectionCode | null {
  // A browser reports `type` inconsistently for CSV (empty, `text/csv`, or an Excel
  // type when Excel is the registered handler), so the name is the primary rule and a
  // declared `text/csv` is accepted alongside it. Neither is trusted by the backend,
  // which sniffs the bytes itself.
  if (!hasCsvName(file.name) && file.type !== 'text/csv') return 'not-csv';
  if (file.size === 0) return 'empty-file';
  if (file.size > MAX_ATTACHMENT_BYTES) return 'too-large';
  return null;
}

/** The file's text, refusing anything that does not decode cleanly as UTF-8. */
export async function readCsvText(file: File): Promise<string> {
  const rejection = csvRejection(file);
  if (rejection) throw new CsvFileError(rejection);

  const text = await file.text();
  try {
    assertSignableText(text, 'not-utf8');
  } catch {
    throw new CsvFileError('not-utf8');
  }
  return text;
}

/** The backend's cap on a client-supplied `unique_id` — see `UploadAttachmentInput.uniqueId`. */
const ATTACHMENT_UNIQUE_ID_MAX_LENGTH = 64;
const ATTACHMENT_UNIQUE_ID_PREFIX = 'csv-';

/**
 * The upload's idempotency key, derived from the file's decoded text: `csv-` plus as many
 * leading hex characters of its SHA-256 as fit under the backend's cap.
 *
 * Deriving the key from the content — instead of minting a random one per selection —
 * makes re-selecting the same file reproduce the same key, whether or not the earlier
 * selection's state survived. A re-upload after "Start over", a retry after a lost
 * response, or a re-import in a later visit all land on the `already-exists` refusal
 * for the recorded attachment and are recovered by `findAttachmentByUniqueId`, rather
 * than reaching the scope-wide `duplicate-file` digest guard, which has no lookup.
 *
 * `attempt` escapes a key the SharedCache still blocks even though nothing usable is on
 * record for it — a `failed` attachment (the digest guard's own recovery is a fresh
 * store, but the plain key stays cache-blocked for 30 days regardless of status) or a
 * record that has since been removed. Call with `attempt` unset for the normal,
 * content-only key; the caller bumps it only once that recovery has come up empty.
 */
export async function uploadUniqueIdFromContent(content: string, attempt = 0): Promise<string> {
  const input = attempt > 0 ? `${content} retry-${attempt}` : content;
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  const hex = bytesToHex(digest).slice(0, ATTACHMENT_UNIQUE_ID_MAX_LENGTH - ATTACHMENT_UNIQUE_ID_PREFIX.length);
  return `${ATTACHMENT_UNIQUE_ID_PREFIX}${hex}`;
}

export interface UploadAttachmentInput {
  fileName: string;
  content: string;
  /**
   * Client-supplied idempotency key, unique per scope, up to 64 characters. A repeat
   * upload carrying the same key is refused `already-exists`, and the key is what lets
   * a timed-out upload be found again with `GET /attachment?unique_id=…` instead of
   * being re-sent into the `duplicate-file` digest guard.
   */
  uniqueId?: string;
  tags?: string[];
}

/**
 * Uploads one CSV as an Attachment.
 *
 * The JSON part is named `attachments`, not the framework default `data` — see
 * b-cell-attachment `validator/attachment/post.py`, which overrides `json_part_name`.
 * The batch envelope is capped at one element on the backend; it exists so the shape
 * stays stable if multipart batching ever becomes safe.
 */
export async function uploadAttachment(
  sessionId: string,
  scopeContext: ScopeContext,
  input: UploadAttachmentInput,
): Promise<Attachment> {
  // `tags` is optional, and the list validator's `min_length` is 1 — so sending an
  // empty list is a validation error (`must be between 1 and 20 objects long`), not a
  // no-op. Omit the key entirely when there is nothing to tag with.
  const attachment: Record<string, unknown> = {};
  if (input.uniqueId) attachment.unique_id = input.uniqueId;
  if (input.tags?.length) attachment.tags = input.tags;

  const multipart = buildMultipartBody([
    {
      name: 'file',
      value: input.content,
      fileName: input.fileName,
      contentType: 'text/csv',
    },
    {
      name: 'attachments',
      value: JSON.stringify({ attachments: [attachment] }),
    },
  ]);

  const response = await backendClient.postMultipart(
    '/attachment', sessionId, multipart, scopeContext,
  );
  if (response.success && response.data) {
    const attachments = requireList<Attachment>(
      response.data as object, 'attachments', 'Unreadable attachment response',
    );
    if (!attachments[0]) {
      throw new BackendValidationError('Unreadable attachment response', UNREADABLE_RESPONSE_CODE);
    }
    return attachments[0];
  }
  if (response.validationError) throw response.validationError;
  throw new BackendValidationError(response.error || 'Failed to upload the file');
}

/**
 * The attachment a previous upload stored under this key, or `null` if none did — or if
 * one exists but its status is not in `statuses`.
 *
 * This is the recovery half of `unique_id`: an upload whose response never arrived is
 * refused `already-exists` on retry, and this read is how the caller finds the
 * attachment that refusal is protecting instead of dead-ending on it. Pass
 * `ADOPTABLE_ATTACHMENT_STATUSES` to keep a `failed` record from being handed back as an
 * adoption target; omit `statuses` for an unfiltered read.
 */
export async function findAttachmentByUniqueId(
  sessionId: string,
  scopeContext: ScopeContext,
  uniqueId: string,
  statuses?: AttachmentStatus[],
): Promise<Attachment | null> {
  const params = new URLSearchParams({ unique_id: uniqueId });
  if (statuses?.length) params.set('status', statuses.join(','));
  const response = await backendClient.get(
    `/attachment?${params}`, sessionId, scopeContext,
  );
  if (response.success && response.data) {
    const attachments = requireList<Attachment>(
      response.data as object, 'attachments', 'Unreadable attachment response',
    );
    return attachments[0] ?? null;
  }
  if (response.validationError) throw response.validationError;
  throw new BackendValidationError(response.error || 'Failed to read the uploaded file');
}

/** One attachment's metadata — the scan poll's read. Answers from `created` onward. */
export async function getAttachmentById(
  sessionId: string,
  scopeContext: ScopeContext,
  attachmentId: string,
): Promise<Attachment> {
  const response = await backendClient.get(
    `/attachment/${attachmentId}`, sessionId, scopeContext,
  );
  if (response.success && response.data) {
    return requireObject<Attachment>(
      response.data as object, 'attachment', 'Unreadable attachment response',
    );
  }
  if (response.validationError) throw response.validationError;
  throw new BackendValidationError(response.error || 'Failed to read the uploaded file');
}
